import React, { FC, useState } from 'react'
import { useForm, ValidationError } from '@formspree/react'
import styled from 'styled-components'
import axios from 'axios'

const IForm = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin: 0;

  label {
    font-size: var(--fs-body-sm);
    font-weight: 500;
    color: #000000;
    margin-bottom: 0.35rem;
  }

  input,
  textarea {
    /* The old rule pinned inputs to 28px tall with 4px padding, so the value
       text had nowhere to sit. Let content set the height instead. */
    width: 100%;
    box-sizing: border-box;
    font-family: var(--font-sans);
    font-size: var(--fs-body);
    line-height: 1.5;
    color: #000000;
    background: #FAFAF8;
    border: 1.5px solid #E2E2E2;
    border-radius: var(--radius-sm);
    padding: 0.7rem 0.9rem;
    margin: 0 0 1.1rem;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;

    &::placeholder {
      color: #828282;
    }

    &:focus {
      outline: none;
      border-color: ${(props) => props.theme.colors.red};
      box-shadow: 0 0 0 3px rgba(153, 0, 0, 0.12);
    }
  }

  textarea {
    resize: vertical;
    min-height: 120px;
  }
`

const Button = styled.button<{ variant: string; size: string }>`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 46px;
  padding: 0.7rem 2rem;
  border: none;
  border-radius: var(--radius-pill);
  background-color: ${(props) =>
    props.variant === 'red'
      ? props.theme.colors.red
      : props.theme.colors.black};
  color: white;
  cursor: pointer;
  font-family: var(--font-sans);
  font-size: var(--fs-body-sm);
  font-weight: 600;
  letter-spacing: 0.01em;
  line-height: 1;
  transition: background-color 0.2s ease, transform 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    background-color: ${(props) =>
      props.variant === 'red' ? '#6B000D' : '#1C1E1F'};
    transform: translateY(-2px);
    box-shadow: 0 8px 18px rgba(0, 0, 0, 0.16);
  }

  &:active {
    transform: translateY(0);
  }
`

const SuccessMessage = styled.div`
  background-color: ${(props) => props.theme.colors.red};
  border-radius: var(--radius-md);
  min-height: 80px;
  padding: 1.25rem;
  width: auto;
  display: flex;
  justify-content: center;
  align-items: center;

  p {
    color: white;
    font-size: var(--fs-heading-sm);
    margin: 0;
  }
`

const Error = styled.div`
  margin-top: 0.25rem;
  padding: 0.5rem;
  text-align: center;
  background: #6B000D;
  color: white;
  border-radius: var(--radius-sm);

  p {
    margin: 0;
  }
`

const Form: FC = () => {
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const [formFields, setFormFields] = useState({
    fullName: '',
    email: '',
    message: ''
  });

  const onChange = (e) => {
    setFormFields({
      ...formFields,
      [e.target.name]: e.target.value
    });
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    if(!formFields.email || !formFields.fullName || !formFields.message){
      setError(true);
      return;
    }

    try {
      const { fullName, email, message } = formFields;
      await axios.post('/sendEmail', { fullName, email, message });
      setError(false);
      setSuccess(true);
      setFormFields({
        fullName: '',
        email: '',
        message: ''
      })
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) { 
      console.error(error)
    }
  }

  if (success) {
    return (
      <SuccessMessage>
        <p>Thanks for your message!</p>
      </SuccessMessage>
    )
  }

  return (
    <IForm id="contact-form" onSubmit={onSubmit}>
      <label htmlFor="fullName">Full Name*</label>
      <input id="fullName" name="fullName" type="text" value={formFields.fullName} onChange={onChange} />
      {/* <ValidationError prefix="Name" field="Full Name" errors={state.errors} /> */}
      <label htmlFor="email">Email Address*</label>
      <input id="email" name="email" type="email" value={formFields.email} onChange={onChange} />
      {/* <ValidationError prefix="Email" field="Email" errors={state.errors} /> */}
      <label htmlFor="message">Message*</label>
      <textarea id="message" name="message" style={{ height: '100px' }} value={formFields.message} onChange={onChange} />
      {/* <ValidationError prefix="Message" field="Message" errors={state.errors} /> */}
      <input type="text" name="_gotcha" style={{ display: 'none' }} />
      {
        error && (
          <Error>
            <p>Please fill in all fields</p>
          </Error>
        )
      }
      <Button
        type="submit"
        variant="red"
        size="sm"
        style={{ marginTop: '0.5rem' }}
      >
        Submit
      </Button>
    </IForm>
  )
}

export default Form
